import type { DashboardFilters } from '@delivery-metrics/shared';
import {
  CloudOff,
  Error as ErrorIcon,
  Refresh,
  TrendingUp,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { FilterPanel } from '../components/FilterPanel';
import { FreshnessIndicator } from '../components/FreshnessIndicator';
import {
  EffortDistributionSection,
  ExecutiveKPISection,
  FeatureDeliverySection,
  ROIAnalysisSection,
} from '../components/leadership';
import { useData } from '../contexts/DataContext';
import { useDashboardByRole } from '../hooks/useDashboard';
import { useDashboardFilters } from '../hooks/useDashboardFilters';
import { useDashboardFreshness } from '../hooks/useDashboardFreshness';
import { MockDataService } from '../services/mock-data';

export const LeadershipDashboard: React.FC = () => {
  const { filters, updateFilters, resetFilters } = useDashboardFilters();
  const [draftFilters, setDraftFilters] = useState<DashboardFilters>(filters);
  const { data, isLoading, error, refetch, hasAccess } =
    useDashboardByRole('Leadership');

  // Sync draft filters when applied filters change (e.g. after reset)
  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  const hasPendingChanges =
    JSON.stringify(draftFilters) !== JSON.stringify(filters);
  const { isRefreshing, hasErrors, clearErrors } = useData();

  // Mock connection status for now
  const connectionStatus = {
    isOnline: true,
    isRealTimeConnected: true,
    realTimeError: null,
    hasOfflineData: false,
    isSyncing: false,
  };

  // Freshness is the server's, not the clock's (US-23).
  const {
    metadata: freshness,
    isLoading: freshnessLoading,
    refetch: refetchFreshness,
  } = useDashboardFreshness();

  const refresh = {
    leadership: refetch,
  };

  // Get available filter options from mock data
  const filterOptions = useMemo(
    () => ({
      teams: MockDataService.getTeams().map(team => ({
        value: team,
        label: team,
      })),
      programs: MockDataService.getPrograms().map(program => ({
        value: program,
        label: program,
      })),
      repositories: MockDataService.getRepositories().map(repo => ({
        value: repo,
        label: repo,
      })),
    }),
    []
  );

  // Calculate derived metrics for enhanced displays
  const derivedMetrics = useMemo((): {
    totalEffort: number;
    totalFeatures: number;
  } | null => {
    if (!data) return null;

    // Type guard to ensure we have LeadershipMetrics
    const leadershipData = data as any; // We'll cast for now since the hook returns union type

    // Effort distribution values are percentages, so total is 100
    const totalEffort = 100;

    const totalFeatures = leadershipData.tShirtSizeDistribution
      ? (
          Object.values(leadershipData.tShirtSizeDistribution) as number[]
        ).reduce((sum: number, count: number) => sum + count, 0)
      : 0;

    return {
      totalEffort,
      totalFeatures,
    };
  }, [data]);

  // Handle access denied
  if (!hasAccess) {
    return (
      <Box>
        <Alert severity='error'>
          You do not have access to the Leadership Dashboard. Please contact
          your administrator.
        </Alert>
      </Box>
    );
  }

  // Handle loading state
  if (isLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <TrendingUp sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant='h4' component='h1'>
            Leadership Dashboard
          </Typography>
          <CircularProgress size={24} sx={{ ml: 2 }} />
        </Box>

        <Typography variant='body1' color='text.secondary' paragraph>
          Executive KPIs, ROI analysis, and feature delivery metrics
        </Typography>

        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={i}>
              <Paper sx={{ p: 3 }}>
                <Skeleton variant='text' width='60%' />
                <Skeleton variant='text' width='40%' height={40} />
                <Skeleton variant='text' width='50%' />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <TrendingUp sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant='h4' component='h1'>
            Leadership Dashboard
          </Typography>
        </Box>

        <Alert
          severity='error'
          action={
            <IconButton color='inherit' size='small' onClick={() => refetch()}>
              <Refresh />
            </IconButton>
          }
        >
          Failed to load dashboard data: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with status indicators */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <TrendingUp sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant='h4' component='h1' sx={{ flexGrow: 1 }}>
          Leadership Dashboard
        </Typography>

        {/* Connection status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!connectionStatus.isOnline && (
            <Chip
              icon={<CloudOff />}
              label='Offline'
              color='warning'
              size='small'
            />
          )}

          {hasErrors && (
            <Chip
              icon={<ErrorIcon />}
              label='Errors'
              color='error'
              size='small'
              onClick={clearErrors}
            />
          )}

          <FreshnessIndicator
            metadata={freshness}
            isLoading={freshnessLoading}
          />

          <IconButton
            onClick={() => {
              refresh.leadership();
              refetchFreshness();
            }}
            disabled={isRefreshing}
            size='small'
          >
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      <Typography variant='body1' color='text.secondary' paragraph>
        Executive KPIs, time efficiency analysis, and feature delivery metrics
      </Typography>

      {/* Filter Panel */}
      <Box sx={{ mb: 4 }}>
        <FilterPanel
          filters={draftFilters}
          onFiltersChange={setDraftFilters}
          onReset={resetFilters}
          onApply={() => updateFilters(draftFilters)}
          applyDisabled={!hasPendingChanges}
          filterConfigs={[
            {
              key: 'timeRange',
              label: 'Time Period',
              type: 'daterange',
              required: true,
              width: 'large',
            },
            {
              key: 'teams',
              label: 'Teams',
              type: 'multiselect',
              options: filterOptions.teams,
              width: 'medium',
            },
            {
              key: 'programs',
              label: 'Programs',
              type: 'multiselect',
              options: filterOptions.programs,
              width: 'medium',
            },
            {
              key: 'repositories',
              label: 'Repositories',
              type: 'multiselect',
              options: filterOptions.repositories,
              width: 'medium',
            },
          ]}
          showPresets={true}
          enableUrlSync={false}
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      {/* Executive KPI Cards */}
      <ExecutiveKPISection data={data as any} loading={isLoading} />

      {/* ROI Analysis Section */}
      <ROIAnalysisSection
        data={data as any}
        totalFeatures={derivedMetrics?.totalFeatures || 0}
        totalEffort={derivedMetrics?.totalEffort || 0}
      />

      {/* Feature Delivery Metrics */}
      <FeatureDeliverySection data={data as any} />

      {/* Effort Distribution and T-Shirt Size Analysis */}
      <EffortDistributionSection
        data={data as any}
        totalEffort={derivedMetrics?.totalEffort || 0}
        totalFeatures={derivedMetrics?.totalFeatures || 0}
      />
    </Box>
  );
};

export default LeadershipDashboard;
